import { Alert, Grid, Stack, Typography } from '@mui/material';
import { ShareRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { NetCard, NetCardLoading } from '@/components/social-profile/NetCard';
import { NetsDocument } from '@/graphql/generated/fetch-net-types.generated';
import type { NetProfileAuthsQuery } from '@/graphql/generated/net-profile-auths.generated';
import { netCodesForCard, visibleNets } from '@/utils/profiles/visible-nets';

interface StepConnectProfileProps {
    profilesData: NetProfileAuthsQuery | undefined;
    loadingProfiles: boolean;
    refetchProfiles: () => void;
}

export const StepConnectProfile = ({
    profilesData,
    loadingProfiles,
    refetchProfiles,
}: StepConnectProfileProps) => {
    const { t } = useTranslation();
    const { data: nets, loading: loadingNets } = useQuery(NetsDocument, {
        variables: {
            onlyPublish: true,
        },
        notifyOnNetworkStatusChange: true,
    });

    const hasConnectedProfiles = (profilesData?.netProfileAuths?.items?.length ?? 0) > 0;

    return (
        <Stack>
            <Stack alignItems="center" py={2} px={2}>
                <Stack alignItems="center" textAlign="center">
                    <Stack
                        sx={{
                            aspectRatio: 1,
                            p: 1.5,
                            borderRadius: '50%',
                            color: 'primary.contrastText',
                            bgcolor: 'primary.light',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <ShareRounded />
                    </Stack>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('onboarding.dev.connect-profile-title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('onboarding.dev.connect-profile-description')}
                    </Typography>
                </Stack>
            </Stack>
            <Stack spacing={2}>
                {hasConnectedProfiles && (
                    <Alert severity="success" variant="outlined">
                        {t('onboarding.dev.first-profile-connected')}
                    </Alert>
                )}
                <Typography variant="caption" color="text.secondary">
                    {t('onboarding.dev.select-network')}
                </Typography>
                <Grid container spacing={2} justifyContent="flex-start">
                    {loadingNets || loadingProfiles ? (
                        <>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <NetCardLoading />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <NetCardLoading />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <NetCardLoading />
                            </Grid>
                        </>
                    ) : (
                        visibleNets(nets?.nets).map((net) => {
                            // The Instagram card stands for both routes into the network, so a
                            // profile connected through Facebook counts as connected here too.
                            const cardCodes = netCodesForCard(net.code);
                            const isConnected =
                                profilesData?.netProfileAuths?.items?.some((profile) =>
                                    cardCodes.includes(profile.netProfile.net.code),
                                ) ?? false;
                            return (
                                <Grid key={net.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <NetCard
                                        net={net}
                                        isConnected={isConnected}
                                        surface="onboarding-dev"
                                        onConnect={() => {
                                            refetchProfiles();
                                        }}
                                    />
                                </Grid>
                            );
                        })
                    )}
                </Grid>
            </Stack>
        </Stack>
    );
};
